import { Fragment, memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import MuiPhoneNumber from "mui-phone-number";
import { useStyles as useTextFieldStyles } from "../TextField/styles";
import VoiceSearch from "../VoiceSearch";
import { getVSLanguageCode, micAdornment } from "../VoiceSearch/utils";
import { AppUser } from "../../pages/api";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useVoiceRecognition } from "../../hooks/useVoiceRecognition";
import { useClickDoubleClick } from "../../hooks/useClickDoubleClick";

export interface PhoneNumberFieldProps {
  field?: any;
  props?: any;
  name?: string;
  label: string;
  placeholder: string;
  required?: boolean;
  error?: boolean;
  helperText?: any;
  value: string;
  onChange: (val: any) => void;
  defaultCountry: string;
  variant: string;
  disabled?: boolean;
  InputProps?: any;
  hasVS?: boolean;
  onBlur?: any;
  style?: any;
  fullWidth?: any;
}

export default memo(
  ({
    field,
    props,
    name,
    label,
    placeholder,
    required = false,
    error = false,
    helperText = "",
    value,
    onChange,
    defaultCountry = "in",
    variant = "outlined",
    disabled = false,
    InputProps,
    hasVS = true,
    // hasVS = false,
    style,
    fullWidth,
  }: PhoneNumberFieldProps) => {
    const textFieldClasses = useTextFieldStyles();
    const { t } = useTranslation();
    const appUser: AppUser = useSelector((state: RootState) => state.appUser);
    const [openVS, setOpenVS] = useState<boolean>(false);

    const {
      isListening,
      isListeningRef,
      language,
      changeLanguage,
      error: voiceError,
      interimText,
      finalText,
      resetText,
      rippleScale,
      start,
      stop,
      restart,
    } = useVoiceRecognition(getVSLanguageCode(t, appUser?.language));

    useEffect(() => {
      const newLang = getVSLanguageCode(t, appUser?.language);
      if (language !== newLang) {
        changeLanguage(newLang);
      }
    }, [appUser?.language]);

    const { handler } = useClickDoubleClick(start, () => setOpenVS(true));

    useEffect(() => {
      if (interimText) {
        if (onChange && typeof onChange === "function") {
          onChange(interimText.replace(/\s/g, "").replace(/-/, ""));
        } else if (props) {
          props.setFieldValue(
            name,
            interimText.replace(/\s/g, "").replace(/-/, ""),
          );
        }
      }
    }, [interimText]);

    return (
      <Fragment>
        <MuiPhoneNumber
          {...field}
          defaultCountry={defaultCountry}
          name={name}
          label={label}
          placeholder={placeholder}
          required={required}
          variant={variant}
          error={error}
          helperText={helperText}
          value={value}
          onChange={(val: string) => {
            onChange(val.replace(/\s/g, "").replace(/-/, ""));
          }}
          size="small"
          inputClass={textFieldClasses.input}
          // style={{ padding: "8.5px 14px", height: "auto" }}
          InputProps={
            InputProps
              ? hasVS &&
                appUser.user?.currentClient?.hasVS &&
                !disabled &&
                ((onChange && typeof onChange === "function") || props)
                ? ({
                    ...InputProps,
                    endAdornment: (
                      <>
                        {micAdornment(
                          handler,
                          null,
                          isListening || isListeningRef.current,
                          stop,
                          rippleScale,
                        )}
                        {InputProps?.endAdornment}
                      </>
                    ),
                  } as any)
                : InputProps
              : hasVS &&
                  appUser.user?.currentClient?.hasVS &&
                  !disabled &&
                  ((onChange && typeof onChange === "function") || props)
                ? {
                    endAdornment: micAdornment(
                      handler,
                      null,
                      isListening || isListeningRef.current,
                      stop,
                      rippleScale,
                    ),
                  }
                : null
          }
          style={style}
          fullWidth
        />
        {openVS && (
          <VoiceSearch
            onClose={() => setOpenVS(false)}
            onOk={(text: string) => {
              if (onChange && typeof onChange === "function") {
                onChange(text.replace(/\s/g, "").replace(/-/, ""));
              } else if (props) {
                props.setFieldValue(
                  name,
                  text.replace(/\s/g, "").replace(/-/, ""),
                );
              }
              setOpenVS(false);
            }}
          />
        )}
      </Fragment>
    );
  },
);
